export interface UserPreferences {
  security_accessibility_mode: boolean;
}

const PREFERENCES_URL = '/api/notes/me/preferences';

class UserPreferencesApiService {
  private async request(method: 'GET' | 'PATCH', value?: boolean, signal?: AbortSignal): Promise<UserPreferences> {
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) controller.abort();
    let timedOut = false;
    const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 15_000);

    try {
      const response = await fetch(PREFERENCES_URL, {
        method,
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json', ...(method === 'PATCH' ? { 'Content-Type': 'application/json' } : {}) },
        ...(method === 'PATCH' ? { body: JSON.stringify({ security_accessibility_mode: value }) } : {}),
        signal: controller.signal,
      });
      if (response.status === 401 || response.status === 403 || response.redirected ||
          (response.ok && !response.headers.get('content-type')?.includes('application/json'))) {
        throw new Error('Your session has expired. Sign in to ARGUS again, then retry.');
      }
      if (!response.ok) throw new Error('Your account preferences could not be reached. Please retry.');
      const data: unknown = await response.json();
      if (!data || typeof data !== 'object' || !('security_accessibility_mode' in data) ||
          typeof data.security_accessibility_mode !== 'boolean') {
        throw new Error('The server returned an invalid preference. Please retry.');
      }
      if (method === 'PATCH' && data.security_accessibility_mode !== value) {
        throw new Error('The server did not confirm your selected preference. Please retry.');
      }
      return { security_accessibility_mode: data.security_accessibility_mode };
    } catch (error) {
      if (timedOut) throw new Error('Your account preferences took too long to respond. Please retry.');
      if (error instanceof TypeError) throw new Error('Your account preferences could not be reached. Please retry.');
      if (error instanceof SyntaxError) throw new Error('The server returned an invalid preference. Please retry.');
      throw error;
    } finally {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', abort);
    }
  }

  get(signal?: AbortSignal): Promise<UserPreferences> {
    return this.request('GET', undefined, signal);
  }

  setSecurityAccessibilityMode(enabled: boolean, signal?: AbortSignal): Promise<UserPreferences> {
    return this.request('PATCH', enabled, signal);
  }
}

export const userPreferencesApi = new UserPreferencesApiService();
